/** Globe math (spec §7.1), ported from the approved mockup: camera rotation, perspective projection, depth, great-circle arcs and
 * even placement on the unit sphere. Pure; screen units are the frame's CSS pixels. */
export type Vec3 = readonly [number, number, number];
export interface Angles {
  yaw: number;
  pitch: number;
}
export interface Camera extends Angles {
  zoom: number;
  cx: number;
  cy: number;
  radius: number;
}
export interface ScreenPoint {
  x: number;
  y: number;
  z: number;
}
/** DEPTH: perspective distance in globe radii (mockup); FOCUS_LIFT: the focus stops ≈15° above centre, so it never covers You. */
export const [DEPTH, FOCUS_LIFT, ZOOM_MIN, ZOOM_MAX] = [5, 0.26, 0.6, 3];
const HALF_PI = Math.PI / 2;
export const clamp = (value: number, low: number, high: number) =>
  Math.max(low, Math.min(high, value));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const wrapAngle = (angle: number) =>
  Math.atan2(Math.sin(angle), Math.cos(angle));
export const chord = (a: Vec3, b: Vec3) =>
  Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
export const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
export function normalize(v: Vec3): Vec3 {
  const length = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / length, v[1] / length, v[2] / length];
}
export function smoothstep(
  edge0: number,
  edge1: number,
  value: number,
): number {
  const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}
/** Yaw about the vertical axis, then pitch; the result is in view space (x right, y down, z toward the viewer). */
export function rotate(p: Vec3, { yaw, pitch }: Angles): Vec3 {
  const x1 = p[0] * Math.cos(yaw) + p[2] * Math.sin(yaw);
  const z1 = -p[0] * Math.sin(yaw) + p[2] * Math.cos(yaw);
  return [
    x1,
    p[1] * Math.cos(pitch) - z1 * Math.sin(pitch),
    p[1] * Math.sin(pitch) + z1 * Math.cos(pitch),
  ];
}
export function unrotate(v: Vec3, { yaw, pitch }: Angles): Vec3 {
  const z1 = -v[1] * Math.sin(pitch) + v[2] * Math.cos(pitch);
  return [
    v[0] * Math.cos(yaw) - z1 * Math.sin(yaw),
    v[1] * Math.cos(pitch) + v[2] * Math.sin(pitch),
    v[0] * Math.sin(yaw) + z1 * Math.cos(yaw),
  ];
}
/** Screen position and depth (z: 1 front, −1 back) of a sphere point (mockup `projector`). */
export function project(p: Vec3, camera: Camera): ScreenPoint {
  const [x, y, z] = rotate(p, camera);
  const scale = camera.radius * camera.zoom * (DEPTH / (DEPTH - z));
  return { x: camera.cx + x * scale, y: camera.cy + y * scale, z };
}
/** The camera angles that bring p to the front centre (mockup `frontAngles`). */
export function frontAngles(p: Vec3): Angles {
  return {
    yaw: Math.atan2(-p[0], p[2]),
    pitch: Math.atan2(p[1], Math.hypot(p[0], p[2])),
  };
}
/** The sphere point at the front centre for these angles: the spot in front of You. */
export function frontPoint(angles: Angles): Vec3 {
  return unrotate([0, 0, 1], angles);
}
/** Where the camera turns to focus p: p in front, FOCUS_LIFT above centre (mockup `applyFocus`). */
export function focusAngles(p: Vec3): Angles {
  const front = frontAngles(p);
  return {
    yaw: front.yaw,
    pitch: clamp(front.pitch + FOCUS_LIFT, -HALF_PI, HALF_PI),
  };
}
/** The default view: the most connected thing a little right of and below centre (mockup `GL.home`). */
export function homeAngles(anchor: Vec3 | null): Angles {
  if (!anchor) return { yaw: 0, pitch: 0 };
  const front = frontAngles(anchor);
  return { yaw: front.yaw + 0.55, pitch: clamp(front.pitch + 0.12, -1.3, 1.3) };
}
/** Front things are readable; back ones shrink to 70% and fade to .25 (spec §7.1, mockup `depthOf`). */
export function depthLook(z: number): { scale: number; alpha: number } {
  const d = smoothstep(-0.18, 0.18, z);
  return { scale: 0.7 + 0.3 * d, alpha: 0.25 + 0.75 * d };
}
/** Points along the surface from a to b, at most 0.07 rad apart (mockup `arcFor`); opposite points go round via a pole. */
export function greatCircle(a: Vec3, b: Vec3): Vec3[] {
  const theta = Math.acos(
    clamp(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1),
  );
  if (theta > Math.PI - 0.01) {
    const middle = normalize(
      cross(a, Math.abs(a[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]),
    );
    return [...greatCircle(a, middle), ...greatCircle(middle, b).slice(1)];
  }
  const steps = Math.max(2, Math.ceil(theta / 0.07));
  const sin = Math.sin(theta);
  return Array.from({ length: steps + 1 }, (_, index): Vec3 => {
    const t = index / steps;
    const wa = theta > 1e-6 ? Math.sin((1 - t) * theta) / sin : 1 - t;
    const wb = theta > 1e-6 ? Math.sin(t * theta) / sin : t;
    return [
      wa * a[0] + wb * b[0],
      wa * a[1] + wb * b[1],
      wa * a[2] + wb * b[2],
    ];
  });
}
/** n points spread evenly on the unit sphere, in bands, so neighbours in the list start near each other. */
export function fibonacciSphere(n: number): Vec3[] {
  const golden = Math.PI * (3 - Math.sqrt(5));
  const points = Array.from({ length: n }, (_, index): Vec3 => {
    const y = 1 - (2 * (index + 0.5)) / n;
    const r = Math.sqrt(1 - y * y);
    return [Math.cos(index * golden) * r, y, Math.sin(index * golden) * r];
  });
  const bands = Math.max(1, Math.round(Math.sqrt(n / 2)));
  const band = (p: Vec3) =>
    Math.min(bands - 1, Math.floor(((1 - p[1]) / 2) * bands));
  return points.sort(
    (p, q) =>
      band(p) - band(q) ||
      (band(p) % 2 ? -1 : 1) *
        (Math.atan2(p[2], p[0]) - Math.atan2(q[2], q[0])),
  );
}
/** Springs pull linked points together while every pair pushes apart; points stay on the sphere (mockup `relax`). */
export function relaxOnSphere(
  points: readonly Vec3[],
  links: ReadonlyArray<readonly [number, number]>,
  iterations = 420,
): Vec3[] {
  const n = points.length;
  const P = new Float64Array(n * 3);
  points.forEach((p, index) => P.set(p, index * 3));
  const L = Math.sqrt((4 * Math.PI) / Math.max(n, 1)) * 0.9;
  const F = new Float64Array(n * 3);
  let maxStep = L * 0.3;
  const push = (i: number, dx: number, dy: number, dz: number, m: number) => {
    F[i * 3] = F[i * 3]! + dx * m;
    F[i * 3 + 1] = F[i * 3 + 1]! + dy * m;
    F[i * 3 + 2] = F[i * 3 + 2]! + dz * m;
  };
  for (let iteration = 0; iteration < iterations; iteration++) {
    F.fill(0);
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const dx = P[i * 3]! - P[j * 3]!,
          dy = P[i * 3 + 1]! - P[j * 3 + 1]!,
          dz = P[i * 3 + 2]! - P[j * 3 + 2]!;
        const r2 = dx * dx + dy * dy + dz * dz + 1e-6;
        const m = (L * L) / (r2 * Math.sqrt(r2));
        push(i, dx, dy, dz, m);
        push(j, dx, dy, dz, -m);
      }
    }
    for (const [a, b] of links) {
      const dx = P[b * 3]! - P[a * 3]!,
        dy = P[b * 3 + 1]! - P[a * 3 + 1]!,
        dz = P[b * 3 + 2]! - P[a * 3 + 2]!;
      const r = Math.sqrt(dx * dx + dy * dy + dz * dz) + 1e-9;
      const m = (1.2 * (r - L * 0.8)) / (r * L);
      push(a, dx, dy, dz, m);
      push(b, dx, dy, dz, -m);
    }
    for (let i = 0; i < n; i++) {
      const px = P[i * 3]!,
        py = P[i * 3 + 1]!,
        pz = P[i * 3 + 2]!;
      const dot = F[i * 3]! * px + F[i * 3 + 1]! * py + F[i * 3 + 2]! * pz;
      const fx = F[i * 3]! - dot * px,
        fy = F[i * 3 + 1]! - dot * py,
        fz = F[i * 3 + 2]! - dot * pz;
      const magnitude = Math.hypot(fx, fy, fz);
      if (magnitude < 1e-9) continue;
      const step = Math.min(magnitude * L * 0.06, maxStep) / magnitude;
      const [x, y, z] = normalize([
        px + fx * step,
        py + fy * step,
        pz + fz * step,
      ]);
      P[i * 3] = x;
      P[i * 3 + 1] = y;
      P[i * 3 + 2] = z;
    }
    maxStep *= 0.992;
  }
  return Array.from(
    { length: n },
    (_, index): Vec3 => [P[index * 3]!, P[index * 3 + 1]!, P[index * 3 + 2]!],
  );
}
